import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import i18n from '../i18n';

const RestaurantAuthContext = createContext();

export const useRestaurantAuth = () => {
    const context = useContext(RestaurantAuthContext);
    if (!context) {
        throw new Error('useRestaurantAuth must be used within a RestaurantAuthProvider');
    }
    return context;
};

export const RestaurantAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [restaurantData, setRestaurantData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
    const justLoggedInRef = useRef(false);

    // Vérifier l'authentification au chargement
    useEffect(() => {
        // Éviter les vérifications multiples
        if (hasCheckedAuth) {
            return;
        }

        // Si on vient juste de se connecter, ne pas vérifier immédiatement
        if (justLoggedInRef.current) {
            console.log('✅ Just logged in, skipping auth check');
            setLoading(false);
            setHasCheckedAuth(true);
            return;
        }

        const checkAuth = async () => {
            console.log('🔍 Checking restaurant auth...');
            setLoading(true);
            const restaurantToken = localStorage.getItem('restaurantToken');
            const justLoggedIn = sessionStorage.getItem('restaurantJustLoggedIn') === 'true';
            
            if (restaurantToken) {
                console.log('🔑 Token found in localStorage');
                
                // Si on vient juste de se connecter, ne pas vérifier immédiatement
                if (justLoggedIn) {
                    console.log('✅ Just logged in (from sessionStorage), skipping auth check');
                    setIsAuthenticated(true);
                    setLoading(false);
                    setHasCheckedAuth(true);
                    // Supprimer le flag après un délai
                    setTimeout(() => {
                        sessionStorage.removeItem('restaurantJustLoggedIn');
                    }, 2000);
                    return;
                }
                
                // Vérifier si on est sur une page restaurant pour éviter les appels inutiles
                const isRestaurantPage = window.location.pathname.startsWith('/management') || 
                                       window.location.pathname.startsWith('/restaurant');
                
                if (!isRestaurantPage) {
                    // Si on n'est pas sur une page restaurant, on assume que l'utilisateur est authentifié
                    // mais on ne fait pas d'appel API
                    console.log('✅ Not on restaurant page, assuming authenticated');
                    setIsAuthenticated(true);
                    setLoading(false);
                    setHasCheckedAuth(true);
                    return;
                }
                
                try {
                    // Token attendu par /management/* = token utilisateur (sub=user_id). Vérifier via /management/profile
                    console.log('📡 Calling /management/profile...');
                    const response = await fetch(`${API_BASE_URL}/management/profile`, {
                        headers: {
                            'Authorization': `Bearer ${restaurantToken}`,
                            'Content-Type': 'application/json',
                        }
                    });

                    console.log('📡 Response status:', response.status);

                    if (response.ok) {
                        const restaurantInfo = await response.json();
                        console.log('✅ Restaurant info retrieved:', restaurantInfo);
                        setRestaurantData(restaurantInfo);
                        setIsAuthenticated(true);
                    } else if (response.status === 401 || response.status === 403) {
                        // Token invalide ou expiré - supprimer le token seulement si on n'est pas en train de se connecter
                        // Éviter de supprimer le token immédiatement après le login
                        const isLoginPage = window.location.pathname === '/business/login';
                        if (!isLoginPage) {
                            console.warn('❌ Token invalide ou expiré, déconnexion...');
                            localStorage.removeItem('restaurantToken');
                            localStorage.removeItem('restaurantEmail');
                            sessionStorage.removeItem('restaurantJustLoggedIn');
                            setIsAuthenticated(false);
                            setRestaurantData(null);
                        } else {
                            // Si on est sur la page de login, garder le token (peut être un problème temporaire)
                            console.log('⚠️ On login page, keeping token');
                            setIsAuthenticated(true);
                        }
                    } else {
                        // Autre erreur (500, 404, etc.) - garder le token et marquer comme authentifié
                        // pour éviter les boucles de redirection
                        console.warn('⚠️ Erreur serveur lors de la vérification auth:', response.status);
                        setIsAuthenticated(true);
                        // Garder les données existantes si disponibles
                    }
                } catch (error) {
                    console.error('❌ Erreur lors de la vérification restaurant:', error);
                    // En cas d'erreur réseau, garder le token et marquer comme authentifié
                    // pour éviter les boucles de redirection
                    setIsAuthenticated(true);
                }
            } else {
                // Pas de token, marquer comme non authentifié
                console.log('❌ No token found');
                setIsAuthenticated(false);
                setRestaurantData(null);
                sessionStorage.removeItem('restaurantJustLoggedIn');
            }
            
            setLoading(false);
            setHasCheckedAuth(true);
        };

        // Délai pour éviter les vérifications trop rapides après le login
        const timeoutId = setTimeout(() => {
            checkAuth();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async (email, password, turnstileToken) => {
        try {
            // Connexion via /restaurants/login (token restaurant_id, résolu vers le
            // propriétaire par get_current_user) — nécessaire pour /management/*
            console.log('🔐 Attempting restaurant login...');
            const response = await fetch(`${API_BASE_URL}/restaurants/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept-Language': i18n.language },
                body: JSON.stringify({ email, password, turnstile_token: turnstileToken }),
            });

            const data = await response.json().catch(() => ({}));
            console.log('📡 Login response:', { status: response.status, data });

            if (response.ok) {
                // 2FA requis — retourner le temp_token pour que la page login affiche l'étape OTP
                if (data.requires_2fa) {
                    return {
                        success: false,
                        requires_2fa: true,
                        temp_token: data.temp_token || '',
                        masked_email: email ? `***@${email.split('@')[1] || '***'}` : '***@***',
                    };
                }

                const token = data.access_token || data.token;
                if (!token) {
                    console.error('❌ No token in response');
                    return {
                        success: false,
                        error: 'Token non reçu du serveur',
                    };
                }

                console.log('✅ Token received, storing...');
                localStorage.setItem('restaurantToken', token);
                localStorage.setItem('restaurantEmail', email);

                sessionStorage.setItem('restaurantJustLoggedIn', 'true');
                justLoggedInRef.current = true;
                setHasCheckedAuth(false);

                // Récupérer le profil restaurant via /management/profile (compatible token user)
                try {
                    console.log('📡 Fetching restaurant info...');
                    const profileResponse = await fetch(`${API_BASE_URL}/management/profile`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    if (profileResponse.ok) {
                        const restaurantInfo = await profileResponse.json();
                        console.log('✅ Restaurant info retrieved:', restaurantInfo);
                        setRestaurantData(restaurantInfo);
                        setIsAuthenticated(true);
                        return { success: true, data: restaurantInfo };
                    }
                    if (profileResponse.status === 403) {
                        localStorage.removeItem('restaurantToken');
                        localStorage.removeItem('restaurantEmail');
                        const errData = await profileResponse.json().catch(() => ({}));
                        return {
                            success: false,
                            error: errData.detail || 'Ce compte n\'est pas associé à un restaurant.',
                        };
                    }
                    if (profileResponse.status === 401) {
                        localStorage.removeItem('restaurantToken');
                        localStorage.removeItem('restaurantEmail');
                        const errData = await profileResponse.json().catch(() => ({}));
                        return {
                            success: false,
                            error: errData.detail || 'Session invalide. Réessayez.',
                        };
                    }
                    // Autre erreur : on garde le token, l’info pourra être récupérée plus tard
                    console.warn('⚠️ Impossible de récupérer le profil restaurant. Status:', profileResponse.status);
                    setIsAuthenticated(true);
                    return { success: true, data: null };
                } catch (meError) {
                    console.warn('⚠️ Erreur lors de la récupération des infos:', meError);
                    setIsAuthenticated(true);
                    return { success: true, data: null };
                }
            } else {
                const detail = Array.isArray(data.detail) ? data.detail[0]?.msg || data.detail : data.detail;
                console.error('❌ Login failed:', data);
                return {
                    success: false,
                    error: detail || data.message || 'Email ou mot de passe incorrect',
                };
            }
        } catch (error) {
            console.error('❌ Erreur de connexion:', error);
            return {
                success: false,
                error: 'Erreur de connexion au serveur',
            };
        }
    };

    const loginWith2FA = async (tempToken, code) => {
        try {
            const res = await fetch(`${API_BASE_URL}/auth/2fa/verify-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ temp_token: tempToken, code }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) return { success: false, error: data.detail || 'Code invalide' };

            const token = data.access_token;
            if (!token) return { success: false, error: 'Token non reçu' };

            localStorage.setItem('restaurantToken', token);
            sessionStorage.setItem('restaurantJustLoggedIn', 'true');
            justLoggedInRef.current = true;
            setHasCheckedAuth(false);

            try {
                const profileRes = await fetch(`${API_BASE_URL}/management/profile`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                if (profileRes.ok) {
                    const info = await profileRes.json();
                    setRestaurantData(info);
                }
            } catch {}
            setIsAuthenticated(true);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message || 'Erreur de connexion' };
        }
    };

    const logout = () => {
        console.log('🚪 Logging out restaurant...');
        localStorage.removeItem('restaurantToken');
        localStorage.removeItem('restaurantEmail');
        sessionStorage.removeItem('restaurantJustLoggedIn');
        setIsAuthenticated(false);
        setRestaurantData(null);
        justLoggedInRef.current = false;
        setHasCheckedAuth(false);
    };

    const getAuthHeaders = () => {
        const restaurantToken = localStorage.getItem('restaurantToken');
        
        return {
            'Authorization': `Bearer ${restaurantToken}`,
            'Content-Type': 'application/json',
        };
    };

    const value = {
        isAuthenticated,
        restaurantData,
        loading,
        login,
        loginWith2FA,
        logout,
        getAuthHeaders
    };

    return (
        <RestaurantAuthContext.Provider value={value}>
            {children}
        </RestaurantAuthContext.Provider>
    );
}; 